import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { BookOpen, Plus, Pencil, Trash2, Search, Filter, GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  L1: 'bg-blue-100 text-blue-700',
  L2: 'bg-green-100 text-green-700',
  L3: 'bg-purple-100 text-purple-700',
  M1: 'bg-orange-100 text-orange-700',
  M2: 'bg-red-100 text-red-700',
};

export const CoursesTab = ({ institutionId }) => {
  const [courses, setCourses] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [dlg, setDlg] = useState(false);
  const [editDlg, setEditDlg] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', level: 'L1', credits: 3, department_unit_id: '' });

  const load = useCallback(async () => {
    try {
      const [coursesRes, unitsRes] = await Promise.all([
        axios.get(`${API}/core/institutions/${institutionId}/courses`),
        axios.get(`${API}/core/institutions/${institutionId}/units-tree`),
      ]);
      setCourses(coursesRes.data);
      // Flatten units tree to get departments
      const flatUnits = [];
      const flatten = (nodes, parentName) => {
        nodes.forEach(n => {
          flatUnits.push({ ...n, parentName });
          if (n.children) flatten(n.children, n.name);
        });
      };
      flatten(unitsRes.data, '');
      setUnits(flatUnits.filter(u => u.type === 'department'));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const filtered = courses.filter(c => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.code.toLowerCase().includes(search.toLowerCase())) return false;
    if (levelFilter !== 'all' && c.level !== levelFilter) return false;
    if (deptFilter !== 'all' && c.department_unit_id !== deptFilter) return false;
    return true;
  });

  // Group by level
  const grouped = {};
  filtered.forEach(c => {
    const lev = c.level || 'Autre';
    if (!grouped[lev]) grouped[lev] = [];
    grouped[lev].push(c);
  });

  const create = async () => {
    try {
      await axios.post(`${API}/core/institutions/${institutionId}/courses`, form);
      toast.success(`Cours "${form.name}" cree`);
      setDlg(false);
      setForm({ name: '', code: '', level: 'L1', credits: 3, department_unit_id: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const update = async () => {
    if (!editCourse) return;
    try {
      await axios.put(`${API}/core/institutions/${institutionId}/courses/${editCourse.id}`, {
        name: form.name, code: form.code, level: form.level, credits: form.credits,
      });
      toast.success('Cours mis a jour');
      setEditDlg(false);
      setEditCourse(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce cours ?')) return;
    try {
      await axios.delete(`${API}/core/institutions/${institutionId}/courses/${id}`);
      toast.success('Cours supprime');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = (c) => {
    setEditCourse(c);
    setForm({ name: c.name, code: c.code, level: c.level, credits: c.credits, department_unit_id: c.department_unit_id });
    setEditDlg(true);
  };

  const getDeptName = (id) => units.find(u => u.id === id)?.name || '';

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="courses-tab">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <Input className="pl-9" placeholder="Rechercher un cours..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-courses" />
        </div>
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Niveau</SelectItem>
            {['L1','L2','L3','M1','M2'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Departement" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les departements</SelectItem>
            {units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={() => { setForm({ name: '', code: '', level: 'L1', credits: 3, department_unit_id: '' }); setDlg(true); }} data-testid="create-course-btn">
          <Plus className="w-4 h-4 mr-1" /> Cours
        </Button>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-gray-500">
        <span><BookOpen className="w-4 h-4 inline mr-1" />{courses.length} cours total</span>
        <span>{filtered.length} affiche{filtered.length > 1 ? 's' : ''}</span>
        {courses.filter(c => c.is_from_catalog).length > 0 && (
          <Badge variant="secondary" className="text-[10px]">{courses.filter(c => c.is_from_catalog).length} depuis le catalogue</Badge>
        )}
      </div>

      {/* Courses grouped by level */}
      {Object.keys(grouped).length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400 text-sm">
          {courses.length === 0 ? "Aucun cours. Les cours seront automatiquement crees lors de l'ajout d'une institution depuis le catalogue RDC." : "Aucun resultat pour ce filtre."}
        </CardContent></Card>
      ) : (
        <ScrollArea className="h-[450px]">
          <div className="space-y-4">
            {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([level, lvlCourses]) => (
              <div key={level}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={LEVEL_COLORS[level] || 'bg-gray-100 text-gray-600'}>{level}</Badge>
                  <span className="text-xs text-gray-400">{lvlCourses.length} cours</span>
                </div>
                <div className="space-y-1">
                  {lvlCourses.map(c => (
                    <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50 group" data-testid={`course-${c.id}`}>
                      <GraduationCap className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{c.name}</span>
                          <Badge variant="outline" className="text-[10px]">{c.code}</Badge>
                          {c.is_from_catalog && <Badge variant="secondary" className="text-[9px]">Catalogue</Badge>}
                        </div>
                        {getDeptName(c.department_unit_id) && (
                          <p className="text-[10px] text-gray-400 truncate">{getDeptName(c.department_unit_id)}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{c.credits} cr</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openEdit(c)}><Pencil className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => del(c.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Create Dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="create-course-dialog">
          <DialogHeader><DialogTitle>Nouveau Cours</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom du cours *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Ex: Algorithmique et Programmation" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Code</Label><Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value}))} placeholder="INFO101" /></div>
              <div><Label>Niveau</Label>
                <Select value={form.level} onValueChange={v => setForm(p => ({...p, level: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['L1','L2','L3','M1','M2'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Credits</Label><Input type="number" value={form.credits} onChange={e => setForm(p => ({...p, credits: parseInt(e.target.value) || 0}))} /></div>
            </div>
            <div><Label>Departement</Label>
              <Select value={form.department_unit_id} onValueChange={v => setForm(p => ({...p, department_unit_id: v}))}>
                <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
                <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annuler</Button>
            <Button onClick={create} disabled={!form.name}>Creer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDlg} onOpenChange={setEditDlg}>
        <DialogContent data-testid="edit-course-dialog">
          <DialogHeader><DialogTitle>Modifier le Cours</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Code</Label><Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value}))} /></div>
              <div><Label>Niveau</Label>
                <Select value={form.level} onValueChange={v => setForm(p => ({...p, level: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['L1','L2','L3','M1','M2'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Credits</Label><Input type="number" value={form.credits} onChange={e => setForm(p => ({...p, credits: parseInt(e.target.value) || 0}))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(false)}>Annuler</Button>
            <Button onClick={update}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
